import { Link, useLocation } from 'react-router-dom';
import { LoginArea } from '@/components/auth/LoginArea';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { BookOpen, Upload, PenLine } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { user } = useCurrentUser();
  const location = useLocation();

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 w-full border-b bg-background/80 backdrop-blur-md">
        <div className="container flex h-16 items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2.5 group">
              <div className="size-8 rounded-lg bg-primary flex items-center justify-center">
                <BookOpen className="size-4 text-primary-foreground" />
              </div>
              <span className="text-xl font-bold font-serif tracking-tight group-hover:text-primary transition-colors">
                Substr
              </span>
            </Link>

            <nav className="hidden sm:flex items-center gap-1">
              <Link to="/">
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    'text-muted-foreground',
                    location.pathname === '/' && 'text-foreground bg-accent',
                  )}
                >
                  <BookOpen className="size-4 mr-1.5" />
                  Feed
                </Button>
              </Link>
              {user && (
                <>
                  <Link to="/import">
                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn(
                        'text-muted-foreground',
                        location.pathname === '/import' && 'text-foreground bg-accent',
                      )}
                    >
                      <Upload className="size-4 mr-1.5" />
                      Import
                    </Button>
                  </Link>
                  <Link to="/my-articles">
                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn(
                        'text-muted-foreground',
                        location.pathname === '/my-articles' && 'text-foreground bg-accent',
                      )}
                    >
                      <PenLine className="size-4 mr-1.5" />
                      My Articles
                    </Button>
                  </Link>
                </>
              )}
            </nav>
          </div>

          <LoginArea className="max-w-60" />
        </div>
      </header>

      <main className="container py-8">
        {children}
      </main>

      <footer className="border-t py-8 mt-16">
        <div className="container text-center text-sm text-muted-foreground">
          <p>
            Vibed with{' '}
            <a
              href="https://shakespeare.diy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 hover:text-foreground transition-colors"
            >
              Shakespeare
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
